import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        mb: 1,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontSize: 12,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const ChipList = ({ items, emptyText }) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {items.map((item) => (
        <Chip
          key={item._id || item.name}
          size="small"
          variant="outlined"
          label={item.name}
        />
      ))}
    </Stack>
  );
};

const StatBlock = ({ label, children, color }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={700} color={color}>
      {children}
    </Typography>
  </Box>
);

const ProductDetailsModal = ({
  open,
  onClose,
  product,
  currency,
  onEdit,
  onDelete,
  onDiscount,
}) => {
  const { t } = useTranslation();

  if (!product) return null;

  const hasAction = onEdit || onDelete || onDiscount;

  const hasDiscount = Boolean(product.discountValue) && product.discountValue > 0;
  const originalPrice = product.originalPrice ?? product.price;
  const currentPrice = hasDiscount
    ? product.discountedPrice
      ? Number(product.discountedPrice).toFixed(2)
      : (Number(originalPrice) - Number(product.discountValue)).toFixed(2)
    : product.price ?? 0;
  const imagePath = (product.image || "").replace(/\\/g, "/");
  const imageSrc = imagePath ? `${apiUrl}/${imagePath}` : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 6 }}>
        {imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt=""
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              objectFit: "cover",
              flexShrink: 0,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ lineHeight: 1.3, wordBreak: "break-word" }}
          >
            {product.name}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.75 }}>
            <Chip
              size="small"
              variant="outlined"
              label={product.visible ? t("filters.visible") : t("filters.hidden")}
              color={product.visible ? "success" : "error"}
            />
            <Chip
              size="small"
              variant="outlined"
              label={product.outOfStock ? t("filters.outOfStock") : t("filters.inStock")}
              color={product.outOfStock ? "error" : "success"}
            />
            {hasDiscount && (
              <Chip size="small" color="success" label={t("fields.activeDiscount")} />
            )}
          </Stack>
        </Box>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Section title={t("fields.sectionDetails")}>
          <Typography
            variant="body2"
            color={product.description ? "text.primary" : "text.secondary"}
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {product.description || t("fields.noDescription")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("fields.productChoice")}{" "}
            <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
              {product.choice === "multiple" ? t("fields.composed") : t("fields.single")}
            </Box>
          </Typography>
        </Section>

        <Section title={t("fields.sectionPricing")}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <StatBlock label={t("fields.price")}>
                {currentPrice} {currency}
              </StatBlock>
            </Grid>
            {product.formulePrice > 0 && (
              <Grid item xs={6} sm={4}>
                <StatBlock label={t("fields.formulePrice")}>
                  {product.formulePrice} {currency}
                </StatBlock>
              </Grid>
            )}
            {product.tva > 0 && (
              <Grid item xs={6} sm={4}>
                <StatBlock label={t("fields.tva")}>{product.tva}%</StatBlock>
              </Grid>
            )}
          </Grid>
        </Section>

        {hasDiscount && (
          <Section title={t("fields.sectionDiscount")}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <StatBlock label={t("fields.originalPrice")} color="error.main">
                  <Box component="span" sx={{ textDecoration: "line-through" }}>
                    {originalPrice} {currency}
                  </Box>
                </StatBlock>
              </Grid>
              <Grid item xs={6} sm={4}>
                <StatBlock label={t("fields.currentPrice")} color="success.main">
                  {currentPrice} {currency}
                </StatBlock>
              </Grid>
              {(product.discountStartDate || product.discountEndDate) && (
                <Grid item xs={12} sm={4}>
                  <StatBlock label={t("fields.period")}>
                    {product.discountStartDate
                      ? dayjs(product.discountStartDate).format("DD/MM/YYYY")
                      : "—"}
                    {" – "}
                    {product.discountEndDate
                      ? dayjs(product.discountEndDate).format("DD/MM/YYYY")
                      : "—"}
                  </StatBlock>
                </Grid>
              )}
            </Grid>
          </Section>
        )}

        <Section title={t("fields.sectionClassification")}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {t("fields.categoriesLabel")}
          </Typography>
          <ChipList items={product.categories} emptyText={t("fields.noCategoryAssigned")} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5, mb: 0.5 }}
          >
            {t("fields.allergiesLabel")}
          </Typography>
          <ChipList items={product.allergies} emptyText={t("fields.noAllergy")} />
        </Section>

        {product.choice === "multiple" && (
          <Section title={t("fields.sectionOptions")}>
            <ChipList items={product.type} emptyText={t("fields.noOptionAssigned")} />
          </Section>
        )}
      </DialogContent>
      {hasAction && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1, justifyContent: "center" }}>
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon fontSize="small" />}
                onClick={onDelete}
              >
                {t("common.delete")}
              </Button>
            )}
            {onDiscount && (
              <Button
                variant="outlined"
                color={hasDiscount ? "success" : "info"}
                startIcon={<LocalOfferIcon fontSize="small" />}
                onClick={onDiscount}
              >
                {t("common.discount")}
              </Button>
            )}
            {onEdit && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<EditIcon fontSize="small" />}
                onClick={onEdit}
              >
                {t("common.edit")}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ProductDetailsModal;
